import {
  toDisplayableOriginalFilename,
  type MessageAttachmentDto,
  type MessageDto,
  type MessageReplyContextDto,
  type MessageTypeName,
} from "@multi-chat/shared";
import {
  firstDocumentAttachment,
  firstStickerAttachment,
  messageHasVoiceAttachment,
} from "../attachment-kind.js";
import { contactCardDisplayName } from "../contact-card.js";
import { isOutboundMessage, messageTypeLabel } from "../inbox-display.js";
import { interactiveFallbackLabel } from "../interactive-fallback-label.js";
import { locationCardTitle } from "../location-card.js";
import { unsupportedMessageLabel } from "../unsupported-label.js";

type Translate = (key: string) => string;

export type ReplyQuoteKind =
  | "text"
  | "image"
  | "video"
  | "voice"
  | "audio"
  | "document"
  | "sticker"
  | "location"
  | "contact"
  | "unsupported"
  | "other";

export type ReplyQuotePresentation = {
  isSelf: boolean;
  authorLabel: string;
  bodyText: string;
  kind: ReplyQuoteKind;
  previewAttachment: MessageAttachmentDto | undefined;
};

function isImageAttachmentType(attachmentType: string): boolean {
  return attachmentType.toUpperCase() === "IMAGE";
}

export function firstImageAttachment(
  attachments: MessageAttachmentDto[] | undefined,
): MessageAttachmentDto | undefined {
  return attachments?.find((attachment) => isImageAttachmentType(attachment.attachmentType));
}

export function replyQuoteKindFromMessage(
  messageType: MessageTypeName | undefined,
  attachments: MessageAttachmentDto[] | undefined,
  unsupportedKind: string | undefined,
): ReplyQuoteKind {
  if (unsupportedKind) {
    return "unsupported";
  }
  switch (messageType) {
    case "TEXT":
    case "INTERACTIVE":
      return "text";
    case "IMAGE":
      return "image";
    case "VIDEO":
      return "video";
    case "AUDIO":
      return messageHasVoiceAttachment(attachments) ? "voice" : "audio";
    case "DOCUMENT":
      return "document";
    case "STICKER":
      return "sticker";
    case "LOCATION":
      return "location";
    case "CONTACT":
      return "contact";
    case "UNKNOWN":
    case undefined:
      return "other";
    default: {
      const _exhaustive: never = messageType;
      return _exhaustive;
    }
  }
}

export function replySelfAuthorLabel(t: Translate): string {
  return t("inbox.replyTo.you");
}

export function replyContactAuthorLabel(
  knownLabel: string | null | undefined,
  t: Translate,
): string {
  const trimmed = knownLabel?.trim();
  if (trimmed) {
    return trimmed;
  }
  return t("inbox.unsavedSender");
}

export function firstImageStickerAttachment(
  attachments: MessageAttachmentDto[] | undefined,
): MessageAttachmentDto | undefined {
  return firstImageAttachment(attachments) ?? firstStickerAttachment(attachments);
}

function previewAttachmentForKind(
  kind: ReplyQuoteKind,
  attachments: MessageAttachmentDto[] | undefined,
): MessageAttachmentDto | undefined {
  if (kind === "image") {
    return firstImageAttachment(attachments);
  }
  if (kind === "sticker") {
    return firstStickerAttachment(attachments);
  }
  return undefined;
}

function bodyTextForQuote(input: {
  kind: ReplyQuoteKind;
  content: string | null | undefined;
  messageType: MessageTypeName | undefined;
  attachments: MessageAttachmentDto[] | undefined;
  contactName?: string | null;
  locationTitle?: string | null;
  unsupportedLabel?: string | null;
  t: Translate;
}): string {
  const { kind, content, messageType, attachments, contactName, locationTitle, unsupportedLabel, t } =
    input;
  const caption = content?.trim();

  switch (kind) {
    case "text": {
      if (caption) {
        return caption;
      }
      if (messageType === "INTERACTIVE") {
        return interactiveFallbackLabel(undefined, t);
      }
      return t("inbox.emptyTextMessage");
    }
    case "image":
      return t("inbox.replyTo.photo");
    case "video":
      return caption ?? t("inbox.replyTo.video");
    case "voice":
      return t("inbox.replyTo.voice");
    case "audio":
      return caption ?? t("inbox.replyTo.audio");
    case "document": {
      const fileName = toDisplayableOriginalFilename(
        firstDocumentAttachment(attachments)?.fileName,
      );
      return fileName || t("inbox.messageType.DOCUMENT");
    }
    case "sticker":
      return t("inbox.replyTo.sticker");
    case "location":
      return locationTitle || t("inbox.location.droppedPin");
    case "contact":
      return contactName || t("inbox.messageType.CONTACT");
    case "unsupported":
      return unsupportedLabel || t("inbox.unsupported.unknown");
    case "other":
      if (caption) {
        return caption;
      }
      if (messageType) {
        return messageTypeLabel(messageType, t);
      }
      return t("inbox.unsupported.unknown");
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function replyQuoteFromMessage(
  message: MessageDto,
  contactLabel: string,
  t: Translate,
): ReplyQuotePresentation {
  const isSelf = isOutboundMessage(message);
  const kind = replyQuoteKindFromMessage(
    message.messageType,
    message.attachments,
    message.unsupportedPresentation?.kind,
  );
  return {
    isSelf,
    authorLabel: isSelf ? replySelfAuthorLabel(t) : replyContactAuthorLabel(contactLabel, t),
    bodyText: bodyTextForQuote({
      kind,
      content: message.content,
      messageType: message.messageType,
      attachments: message.attachments,
      contactName: message.contactPresentation
        ? contactCardDisplayName(message.contactPresentation)
        : null,
      locationTitle: message.locationPresentation
        ? locationCardTitle(message.locationPresentation, t)
        : null,
      unsupportedLabel: message.unsupportedPresentation
        ? unsupportedMessageLabel(message.unsupportedPresentation.kind, t)
        : null,
      t,
    }),
    kind,
    previewAttachment: previewAttachmentForKind(kind, message.attachments),
  };
}

export function replyQuoteFromContext(
  replyContext: MessageReplyContextDto,
  t: Translate,
): ReplyQuotePresentation | null {
  if (replyContext.messageId == null) {
    return null;
  }
  const isSelf = replyContext.direction === "OUTBOUND";
  const preview = replyContext.previewAttachment;
  const contextAttachments = preview ? [preview] : undefined;
  const kind = replyQuoteKindFromMessage(
    replyContext.messageType,
    contextAttachments,
    replyContext.unsupportedPresentation?.kind,
  );
  return {
    isSelf,
    authorLabel: isSelf
      ? replySelfAuthorLabel(t)
      : replyContactAuthorLabel(replyContext.authorLabel, t),
    bodyText: bodyTextForQuote({
      kind,
      content: replyContext.content,
      messageType: replyContext.messageType,
      attachments: contextAttachments,
      contactName: replyContext.contactPresentation
        ? contactCardDisplayName(replyContext.contactPresentation)
        : null,
      locationTitle: replyContext.locationPresentation
        ? locationCardTitle(replyContext.locationPresentation, t)
        : null,
      unsupportedLabel: replyContext.unsupportedPresentation
        ? unsupportedMessageLabel(replyContext.unsupportedPresentation.kind, t)
        : null,
      t,
    }),
    kind,
    previewAttachment: previewAttachmentForKind(kind, contextAttachments),
  };
}
